import { useState, useCallback } from 'react';

/**
 * Custom hook for managing optimistic vote updates on requests
 * Provides immediate UI feedback while vote operations are pending
 */
export function useOptimisticVotes(initialRequests = []) {
  const [requests, setRequests] = useState(initialRequests);
  const [pendingVotes, setPendingVotes] = useState(new Set());

  // Update the entire requests array and clear pending states
  // Fresh server data means all pending operations are resolved
  const updateRequests = useCallback((newRequests) => {
    console.log('useOptimisticVotes: Updating requests with', newRequests.length, 'items');
    if (!Array.isArray(newRequests)) {
      console.error('useOptimisticVotes: newRequests is not an array:', newRequests);
      return;
    }
    setRequests(newRequests);
    // Clear all pending states since we have fresh server data
    setPendingVotes(new Set());
  }, []);

  // Optimistically toggle a vote for a request
  const toggleOptimisticVote = useCallback((requestId) => {
    console.log('useOptimisticVotes: Toggling optimistic vote for request:', requestId);
    
    if (!requestId) {
      console.error('useOptimisticVotes: requestId is required');
      return;
    }

    // Add to pending votes
    setPendingVotes(prev => new Set([...prev, requestId]));

    // Update the request optimistically
    setRequests(prevRequests => {
      return prevRequests.map(request => {
        if (request.id === requestId) {
          const currentVoteCount = request.voteCount || 0;
          const currentUserHasVoted = request.userHasVoted || false;
          
          // Toggle the vote
          const newUserHasVoted = !currentUserHasVoted;
          const newVoteCount = newUserHasVoted 
            ? currentVoteCount + 1 
            : Math.max(0, currentVoteCount - 1);

          console.log('useOptimisticVotes: Optimistic update for request', requestId, {
            oldVoteCount: currentVoteCount,
            newVoteCount,
            oldUserHasVoted: currentUserHasVoted,
            newUserHasVoted
          });

          return {
            ...request,
            voteCount: newVoteCount,
            userHasVoted: newUserHasVoted
          };
        }
        return request;
      });
    });
  }, []);

  // Clear pending state for a specific request
  const clearPending = useCallback((requestId) => {
    console.log('useOptimisticVotes: Clearing pending state for request:', requestId);
    setPendingVotes(prev => {
      const newSet = new Set(prev);
      newSet.delete(requestId);
      return newSet;
    });
  }, []);

  // Check if a request has a pending vote
  const isPending = useCallback((requestId) => {
    return pendingVotes.has(requestId);
  }, [pendingVotes]);

  // Clear all pending votes
  const clearAllPending = useCallback(() => {
    console.log('useOptimisticVotes: Clearing all pending votes');
    setPendingVotes(new Set());
  }, []);

  return {
    requests,
    updateRequests,
    toggleOptimisticVote,
    clearPending,
    isPending,
    clearAllPending
  };
}